// START GENAI@CHATGPT4
import { Component, Prop, h, State, Event, EventEmitter, Fragment } from '@stencil/core';
import { getDataFromPropString, updateListBasedOnCheckboxState } from '@utils/utils';
import { checkQuizDateAnsObj } from '@utils/date.helper';
import { QUIZ_DISPLAY_FORMAT, QUIZ_QUESTION_TYPE } from '@src/components/vs-quiz/vs-quiz.constant';
import { REQUIRED_TEXT } from '@src/components/vs-form-builder/helpers/constant';
import { getFieldValidators } from '@src/components/vs-form-builder/helpers/validator.helper';


@Component({
  tag: 'vs-quiz-element',
  styleUrl: 'vs-quiz-element.css',
  shadow: false,
})
export class VsQuizElement {

  @Prop() question: any;
  @Prop() questionNumber: number;
  @Prop() questionType: string;
  @Prop() questionsTotalNum: number;
  @Prop() quizSubmitButtonLabel: string;
  @Prop() quizNextButtonLabel: string;

  @Prop() quizType: string;

  @State() dateField = {
    name: 'selectedAnswerDate',
    label: 'Enter a valid date',
    rules: {
      'required': 'Please enter any valid date',
      'dateFormat:DD-MM-YYYY': 'Date format should be DD-MM-YYYY',
      'notBefore:DD-MM-YYYY|01-01-1970': 'Please enter any valid date',
      'notAfter:DD-MM-YYYY|31-12-2999': 'Please enter any valid date',
    },
  };
  @State() questionDuration: string;

  @State() questionObj: any;
  @State() quizDescription: string;
  @State() selectedAnswers: any[] = [];
  @State() timeSpent: any;

  /**
   * @shortDescription Emit answers filled for the question
   * @controlName updateAnsweredEvent */
  @Event({
    eventName: 'updateAnsweredEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) onUpdateAnsweredEvent: EventEmitter;


  /**
   * @shortDescription Emit times up event
   * @controlName timesUpEvent */
  @Event({
    eventName: 'timesUpEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) onTimesUpEvent: EventEmitter;


  /**
   * @shortDescription Emit submit all answers when questionnaire reach an end
   * @controlName submitAnsweredEvent */
  @Event({
    eventName: 'submitAnsweredEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) onSubmitAnsweredEvent: EventEmitter;

  componentWillLoad() {
    this.questionObj = getDataFromPropString(this.question);
    if(this.questionObj?.questionDuration) {
      this.questionDuration = this.questionObj?.questionDuration;
    }
  }

  selectSingleChoiceAnswer = (event: any, answerId: any) => {
    if (event?.target?.checked) {
      this.selectedAnswers = [answerId];
    } else {
      this.selectedAnswers = [];
    }
    this.updateAnswerIfDisplayAllQuestions();
  };

  selectMultipleChoiceAnswer = (event: any, answerId: any) => {
    this.selectedAnswers = updateListBasedOnCheckboxState(this.selectedAnswers, answerId, event?.target?.checked);
    this.updateAnswerIfDisplayAllQuestions();
  };

  updateAnswer = (): void => {
    this.onUpdateAnsweredEvent.emit(this.getAnswerObj(this.selectedAnswers));
    if (this.questionObj.questionDuration) {
      this.questionObj.questionDuration = null;
    }
  };

  updateAnswerIfDisplayAllQuestions = () => {
    if (this.quizType === QUIZ_DISPLAY_FORMAT.ALL_TOGETHER) {
      this.updateAnswer();
    }
  };
  submitAnswer = (): void => {
    this.updateAnswer();
    this.onSubmitAnsweredEvent.emit(true);
  };

  /* START GENAI@CHATGPT4 */
  getAnswerObj = (selectedAnswers: any[]) => {
    const processedSelectedAnswers
      = selectedAnswers.map(answer => checkQuizDateAnsObj(answer));

    return {
      key: this.questionObj.id,
      values: processedSelectedAnswers,
      ...(this.timeSpent && { timeSpent: this.timeSpent }),
    };
  };

  /* END GENAI@CHATGPT4 */

  handleTimeCount = (timeCount: any) => {
    this.timeSpent = timeCount;
  };

  /* START GENAI@CHATGPT4 */
  handleTimesUp = () => {
    this.questionObj.questionDuration = null;
    const isLastQuestion = this.questionNumber === (this.questionsTotalNum - 1);
    if (this.quizType === QUIZ_DISPLAY_FORMAT.ONE_AT_A_TIME && !isLastQuestion) {
      this.updateAnswer();
    } else if (isLastQuestion) {
      this.submitAnswer();
    }
  };
  /* END GENAI@CHATGPT4 */

  onDateAnswerFieldChange = (e: CustomEvent) => {
    if (e?.detail?.value) {
      this.selectedAnswers = [e.detail.value];
      this.updateAnswerIfDisplayAllQuestions();
    }
  };


  render() {
    return (
      <Fragment>
        {this.questionDuration && this.quizType === QUIZ_DISPLAY_FORMAT.ONE_AT_A_TIME
          && <vs-quiz-countdown quiz-time={this.questionDuration} onTimesUpEvent={this.handleTimesUp} />
        }
        <fieldset class='questionBody vs-my-1'>
          <br />
          <legend class='vs-hidden' aria-labelledby={`question${this.questionNumber}Label`}></legend>
          <p class='vs-h4'
             id={`question${this.questionNumber}Label`}>{this.questionNumber + 1}. {this.questionObj?.qns} </p>
          <br />
          <hr class='question' />
          <br />
          {
            this.questionObj?.type == QUIZ_QUESTION_TYPE.DATE &&
            <div class='vs-col-12 vs-col-lg-6 vs-p-0 vs-pr-lg-3 vs-form'>
              <vs-input isTextArea={false}
                        isDate={true}
                        type='text'
                        key={this.dateField.name}
                        eyebrow={true}
                        label={this.dateField.label}
                        defaultValue={''}
                        requiredText={REQUIRED_TEXT}
                        onComputedInputValue={this.onDateAnswerFieldChange}
                        validators={getFieldValidators(this.dateField)}
              />
            </div>
          }
          <div class='vs-row'>
            {
              this.questionObj?.options?.map((option: any, index: number) => (
                <Fragment>
                  {
                    this.questionObj?.type === QUIZ_QUESTION_TYPE.SINGLE_CHOICE &&
                    <div class='vs-col-xs-6 vs-col-sm-6 vs-col-md-6 vs-col-lg-6 ans-margin'>
                      <div class='answer'>
                        <input type='radio' id={`answerSingleChoice${this.questionNumber}${index}`}
                               name={`answerSingleChoice${this.questionNumber}`}
                               onChange={(e) => this.selectSingleChoiceAnswer(e, option?.id)}
                        />
                        <label class={`vs-btn-block vs-btn vs-btn-primary dont-break-out
                        ${index % 2 === 0 ? 'pull-left' : 'pull-right'}`}
                               htmlFor={`answerSingleChoice${this.questionNumber}${index}`}
                        >
                          <span class='answerIndex pull-left dont-break-out'></span>
                          {option?.option}
                        </label>
                      </div>
                    </div>
                  }
                  {
                    this.questionObj?.type === QUIZ_QUESTION_TYPE.MULTIPLE_CHOICE &&
                    <div class='vs-col-xs-6 vs-col-sm-6 vs-col-md-6 vs-col-lg-6 margin-bottom-10'>
                      <div class='answer'>
                        <input type='checkbox' id={`answerMultiChoice${this.questionNumber}${index}`}
                               name={`answerMultiChoice${this.questionNumber}${index}`}
                               onChange={(e) => this.selectMultipleChoiceAnswer(e, option?.id)} />
                        <label class={`vs-btn-block vs-btn vs-btn-primary dont-break-out
                        ${index % 2 === 0 ? 'pull-left' : 'pull-right'}`}
                               htmlFor={`answerMultiChoice${this.questionNumber}${index}`}>
                          <span class='answerIndex pull-left  dont-break-out'></span>
                          {option?.option}
                        </label>
                      </div>
                    </div>
                  }</Fragment>
              ))
            }
          </div>
        </fieldset>
        <div class='vs-d-flex vs-justify-content-end'>
          {this.quizType === QUIZ_DISPLAY_FORMAT.ONE_AT_A_TIME
            && this.questionNumber < (this.questionsTotalNum - 1) &&
            <button class='vs-btn vs-btn-primary v-mt-1' type='button' onClick={this.updateAnswer}>
              <strong>{this.quizNextButtonLabel || 'Next'}</strong>
            </button>
          }
          {this.questionNumber === (this.questionsTotalNum - 1) &&
            <button class='vs-btn vs-btn-primary v-mt-1' type='button'
                    id="quizSubmitButton" onClick={this.submitAnswer}>
              <strong>{this.quizSubmitButtonLabel || 'Submit'}</strong>
            </button>
          }
        </div>
      </Fragment>
    )
      ;
  }
}

// END GENAI@CHATGPT4
